import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocale } from '../contexts/LocaleContext';
import { priceCartForPreview } from '../lib/pricing';
import * as types from '../types';
import useHardwareFirstCheckout from './useHardwareFirstCheckout';

const SHOP_CARD_PREVIEW_CART_ITEM_ID = 'hardware-first-shop-card-preview';

type DiscountsBySku = Record<string, types.HardwareDiscountPreview | undefined>;

interface ShopCardsPricing {
  /** each card's price today, under the cart's code when it has one */
  discounts: DiscountsBySku;
  /** each card's price under `offerCode`; empty without one */
  offers: DiscountsBySku;
}

/** one unit priced alone: the promo share holds per unit, and a single unit takes no multi-device saving */
function singleUnitPrice(
  { listPriceInCents, promoDiscountInCents }: types.HardwareDiscountPreview,
  withPromo: boolean,
): types.HardwareDiscountPreview {
  const promo = withPromo ? promoDiscountInCents : 0;
  return {
    listPriceInCents,
    promoDiscountInCents: promo,
    multiDeviceDiscountInCents: 0,
    discountedPriceInCents: listPriceInCents - promo,
  };
}

/**
 * Server-resolved first-unit hardware-first price for each shop card, from one pricing call that
 * holds every sku. `offerCode` is the live promotion's code, only set while the cart has none, so
 * the call carries one code either way and a card's price today is its list price.
 */
export default function useHardwareFirstShopCardsPricing(
  skus: (string | undefined)[],
  offerCode: string | undefined,
): ShopCardsPricing {
  const enrolled = useHardwareFirstCheckout();
  const { currency } = useLocale();
  const cartCouponCode = useSelector((state: types.AppState) => state.storeShop.cart.couponCode);
  const couponCode = offerCode ?? cartCouponCode;
  const [pricing, setPricing] = useState<ShopCardsPricing>({ discounts: {}, offers: {} });
  const skusKey = skus.filter((sku): sku is string => !!sku).join(',');

  useEffect(() => {
    const pricedSkus = skusKey ? skusKey.split(',') : [];
    if (!enrolled || pricedSkus.length === 0) {
      // keeps the state object when it is already empty, so a cart with nothing to price does not re-render
      setPricing((previous) =>
        Object.keys(previous.discounts).length === 0 && Object.keys(previous.offers).length === 0
          ? previous
          : { discounts: {}, offers: {} },
      );
      return undefined;
    }

    let cancelled = false;
    // hardware-first prices are keyed by cart item id, so each card needs its own
    const cardItemId = (sku: string) => `${SHOP_CARD_PREVIEW_CART_ITEM_ID}-${sku}`;
    const cart: types.StoreCart = {
      cartItems: Object.fromEntries(
        pricedSkus.map((sku) => [
          cardItemId(sku),
          { cartItemId: cardItemId(sku), lineItem: { lineItemId: cardItemId(sku), sku }, quantity: 1 },
        ]),
      ),
      currency,
      couponCode,
    };

    const pricesBySku = (priced: types.CartPricing, withPromo: boolean) =>
      Object.fromEntries(
        pricedSkus.map((sku) => {
          const discount = priced.hardwareDiscounts?.[cardItemId(sku)];
          return [sku, discount && singleUnitPrice(discount, withPromo)];
        }),
      );

    const priceCards = async (): Promise<ShopCardsPricing> => {
      try {
        const priced = await priceCartForPreview(cart);
        return offerCode
          ? { discounts: pricesBySku(priced, false), offers: pricesBySku(priced, true) }
          : { discounts: pricesBySku(priced, true), offers: {} };
      } catch {
        // a code the server rejects (expired, or missing from this environment) still leaves list prices
        if (!couponCode) {
          return { discounts: {}, offers: {} };
        }
        try {
          return {
            discounts: pricesBySku(await priceCartForPreview({ ...cart, couponCode: undefined }), false),
            offers: {},
          };
        } catch {
          // already reported inside priceCartForPreview; the cards then omit their price rows
          return { discounts: {}, offers: {} };
        }
      }
    };

    priceCards().then((next) => {
      if (!cancelled) {
        setPricing(next);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [enrolled, skusKey, currency, couponCode, offerCode]);

  return pricing;
}
